import { useState, useEffect, useRef } from 'react';
import { X, RefreshCw, Wrench, Loader2, TriangleAlert, Square, Send, Flag } from 'lucide-react';
import { OLLAMA_BASE, CLAUDE_BASE, streamNdjsonChat, ollamaGenOptions } from '../../utils/llmStream';
import styles from './PromptPanel.module.css';

function CopyableBlock({ label, text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div className={styles.block}>
      <div className={styles.blockHeader}>
        <span className={styles.blockLabel}>{label}</span>
        <button
          className={`${styles.copyBtn} ${copied ? styles.copied : ''}`}
          onClick={handleCopy}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre className={styles.pre}>{text}</pre>
    </div>
  );
}

// Debug prompt blocks shown as tabs so they aren't all stacked in one long
// scroll. `tabs` is [{ id, label, text }]; only one block is shown at a time.
function PromptTabs({ tabs }) {
  const [activeId, setActiveId] = useState(tabs[0]?.id);
  // Keep the selection valid when the tab set changes (rebuild / mode switch).
  useEffect(() => {
    if (!tabs.some(t => t.id === activeId)) setActiveId(tabs[0]?.id);
  }, [tabs, activeId]);

  if (!tabs.length) return null;
  const active = tabs.find(t => t.id === activeId) || tabs[0];

  return (
    <div className={styles.promptTabs}>
      <div className={styles.promptTabBar}>
        {tabs.map(t => (
          <button
            key={t.id}
            className={`${styles.promptTab} ${active.id === t.id ? styles.promptTabActive : ''}`}
            onClick={() => setActiveId(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <CopyableBlock label={active.label} text={active.text} />
    </div>
  );
}

function CapabilitySummary({ capabilities }) {
  if (!capabilities) return null;
  const avail  = capabilities.available_labels || [];
  const absent = capabilities.absent_labels   || [];
  return (
    <div className={styles.capSummary}>
      <span className={styles.capTier}>Tier {capabilities.tier}</span>
      <span className={styles.capAvail} title="Channels recorded — coaching can use these">
        ✓ {avail.join(', ') || 'none'}
      </span>
      {absent.length > 0 && (
        <span className={styles.capAbsent} title="Not recorded — coaching will not comment on these">
          ✗ {absent.join(', ')}
        </span>
      )}
    </div>
  );
}

function PriorityList({ priorities }) {
  if (!priorities?.length) return null;
  return (
    <div className={styles.priorities}>
      <div className={styles.prioritiesTitle}>Top Priorities (ranked by straight-weighted score)</div>
      {priorities.map((p, i) => (
        <div key={i} className={styles.priority}>
          <span className={styles.priorityRank}>#{i + 1}</span>
          <span className={styles.priorityCorner}>{p.corner_id}</span>
          {p.corner_type && (
            <span className={`${styles.cornerType} ${styles['ct_' + p.corner_type]}`}>{p.corner_type}</span>
          )}
          <span className={styles.priorityFlags}>{(p.flags || []).join(', ')}</span>
          <span className={styles.priorityLoss} title="estimated time loss (straight-weighted score)">
            ~{p.estimated_loss_s?.toFixed(2)}s
            {p.priority_score != null && p.priority_score !== p.estimated_loss_s && (
              <span className={styles.priorityScore}> →{p.priority_score.toFixed(2)}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function PromptPanel({ briefing, loading, error, onClose, mode, capabilities, onBuild, canBuild, forceOllama = false }) {
  // How to organise the coaching: 'goal' (top-3 cross-corner goals, default),
  // 'corner' (top-3 corners, drill-down), 'sector' (sector-anchored plan) or
  // 'debrief' (session summary vs the driver's own history at this track).
  // Not applicable to comparison.
  const [coachingMode, setCoachingMode] = useState('goal');

  // Which inference backend to send to: 'ollama' (local) or 'claude' (hosted).
  const [backend, setBackend] = useState('ollama');
  const [claudeStatus, setClaudeStatus] = useState(null);  // { configured, model }

  // How many LLM calls: 'percorner' (one small call per top corner — much more
  // reliable for small local models) or 'single' (the whole briefing at once).
  const [callMode, setCallMode] = useState('percorner');

  // Debug mode shows the raw prompts sent to the LLM; off = just the coaching.
  const [debugMode, setDebugMode] = useState(() => {
    try { return localStorage.getItem('promptPanelDebug') === '1'; } catch { return false; }
  });
  const toggleDebug = () => {
    setDebugMode(d => {
      try { localStorage.setItem('promptPanelDebug', d ? '0' : '1'); } catch { /* ignore */ }
      return !d;
    });
  };

  // Generic stream state (shared across backends).
  const [ollamaStatus,   setOllamaStatus]   = useState(null);
  const [ollamaLoading,  setOllamaLoading]  = useState(false);
  const [ollamaResponse, setOllamaResponse] = useState(null);
  const [ollamaModel,    setOllamaModel]    = useState('');
  const [streamLabel,    setStreamLabel]    = useState('');   // which corner is in flight

  // Model list state
  const [models,        setModels]        = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError,   setModelsError]   = useState(null);

  // Ref to auto-scroll the response area as tokens stream in
  const responseRef = useRef(null);
  // Abort controller so we can cancel a running stream
  const abortRef = useRef(null);

  // Fetch available models from Ollama on mount
  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    setModelsError(null);

    fetch(`${OLLAMA_BASE}/tags`)
      .then(r => {
        if (!r.ok) throw new Error(`Ollama returned ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        const list = (data?.models ?? []).map(m => ({
          name:    m.name,
          size_gb: m.size ? (m.size / 1e9).toFixed(1) : null,
        }));
        setModels(list);
        if (list.length > 0) setOllamaModel(list[0].name);
      })
      .catch(err => {
        if (cancelled) return;
        setModelsError(err.message);
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Check whether the hosted Claude backend is configured (key present).
  // Skipped in forceOllama mode (no-account public/guest) so Claude (paid) is
  // never offered or probed for anonymous users.
  useEffect(() => {
    if (forceOllama) return;
    let cancelled = false;
    fetch(`${CLAUDE_BASE}/status`)
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (!cancelled) setClaudeStatus(s); })
      .catch(() => { if (!cancelled) setClaudeStatus(null); });
    return () => { cancelled = true; };
  }, [forceOllama]);

  // Auto-scroll response area when new tokens arrive
  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [ollamaResponse]);

  // Cancel any in-flight stream when unmounting
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // Stream one NDJSON chat request (shared reader in utils/llmStream.js).
  const streamChat = (url, body, controller, onDelta) =>
    streamNdjsonChat(url, body, controller.signal, onDelta,
                     backend === 'claude' ? 'Claude' : 'Ollama');

  const handleSend = async () => {
    if (!briefing) return;
    const isClaude = backend === 'claude';
    if (!isClaude && !ollamaModel) return;

    // Cancel previous request if still running
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setOllamaLoading(true);
    setOllamaStatus(null);
    setOllamaResponse('');
    setStreamLabel('');

    const url    = isClaude ? `${CLAUDE_BASE}/chat` : `${OLLAMA_BASE}/chat`;
    const chunks = (callMode === 'percorner' && briefing.chunks?.length > 0) ? briefing.chunks : null;
    // Goal mode is a single call with no chunks; it still leads with the
    // (separate) highlight call and appends the Python headline.
    const goalMode = !chunks && briefing.goals?.length > 0 && !!briefing.system_prompt;

    // Generation options (num_ctx growth, temperature 0, seed) live in
    // ollamaGenOptions — see utils/llmStream.js for the num_ctx rationale.
    const makeBody = (system, user, perCorner) => {
      if (isClaude) return { system, user, model: claudeStatus?.model };
      const numPredict = Math.max(1500, perCorner ? 500 : 1500);
      return {
        model: ollamaModel,
        stream: true,
        options: ollamaGenOptions(system, user, numPredict),
        messages: [
          { role: 'system', content: system },
          { role: 'user',   content: user   },
        ],
      };
    };

    let accumulated = '';
    const push = (text) => {
      accumulated += text;
      setOllamaResponse(accumulated);
    };

    try {
      if (chunks) {
        // Lead with the positive. Python SELECTED the strength (the fact);
        // the LLM phrases it warmly. Falls back to the Python sentence verbatim
        // if the phrasing call fails or the backend didn't supply a prompt.
        if (briefing.highlight) {
          setStreamLabel("What You're Doing Well");
          push("## What You're Doing Well\n");
          if (briefing.highlight_system_prompt) {
            const before = accumulated;
            try {
              await streamChat(
                url,
                makeBody(briefing.highlight_system_prompt, briefing.highlight, true),
                controller, push);
            } catch (err) {
              if (err.name === 'AbortError') return;
              if (accumulated === before) push(briefing.highlight);   // fallback
            }
          } else {
            push(briefing.highlight);   // older backend → fixed sentence
          }
          push('\n\n');
        }
        // One call per corner, sequential — each prompt is self-contained.
        for (const chunk of chunks) {
          if (controller.signal.aborted) return;
          setStreamLabel(chunk.label);
          await streamChat(url, makeBody(briefing.chunk_system_prompt, chunk.user_prompt, true),
                           controller, push);
          push('\n\n');
        }
        // The summary is composed by Python — no LLM call needed for it.
        if (briefing.headline) {
          push(`## One Thing to Try First\n${briefing.headline}\n`);
        }
      } else if (goalMode) {
        // Lead with the LLM-phrased positive (Python-selected fact + fallback),
        // then ONE call for the 3 ranked goals, then the Python headline.
        if (briefing.highlight) {
          setStreamLabel("What You're Doing Well");
          push("## What You're Doing Well\n");
          if (briefing.highlight_system_prompt) {
            const before = accumulated;
            try {
              await streamChat(
                url, makeBody(briefing.highlight_system_prompt, briefing.highlight, true),
                controller, push);
            } catch (err) {
              if (err.name === 'AbortError') return;
              if (accumulated === before) push(briefing.highlight);   // fallback
            }
          } else {
            push(briefing.highlight);
          }
          push('\n\n');
        }
        setStreamLabel('Goals');
        await streamChat(url, makeBody(briefing.system_prompt, briefing.user_prompt, false),
                         controller, push);
        if (briefing.headline) {
          push(`\n\n## One Thing to Try First\n${briefing.headline}\n`);
        }
      } else {
        // Debrief leads with the Python-composed headline (instant, model-free),
        // then one call for the full three-section summary.
        if (briefing.mode === 'debrief' && briefing.headline) {
          push(`**${briefing.headline}**\n\n`);
        }
        await streamChat(url, makeBody(briefing.system_prompt, briefing.user_prompt, false),
                         controller, push);
      }
      setOllamaStatus('ok');
    } catch (err) {
      if (err.name === 'AbortError') return;   // user cancelled
      setOllamaStatus('error');
      const hint = backend === 'claude'
        ? `Make sure ANTHROPIC_API_KEY is set in the backend .env.`
        : `Make sure Ollama is running on the server (ollama serve) and a model is installed (ollama pull llama3).`;
      setOllamaResponse(`${accumulated ? accumulated + '\n\n' : ''}Error: ${err.message}\n\n${hint}`);
    } finally {
      setOllamaLoading(false);
      setStreamLabel('');
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setOllamaLoading(false);
  };

  const modelPickerContent = () => {
    if (modelsLoading) {
      return <div className={styles.modelStatus}>Connecting to Ollama…</div>;
    }
    if (modelsError || models.length === 0) {
      return (
        <input
          className={styles.modelInput}
          type="text"
          value={ollamaModel}
          onChange={e => setOllamaModel(e.target.value)}
          placeholder="Model name (e.g. llama3)"
          spellCheck={false}
          title={modelsError ? `Could not load models: ${modelsError}` : 'No models found — type a model name'}
        />
      );
    }
    return (
      <select
        className={styles.modelSelect}
        value={ollamaModel}
        onChange={e => setOllamaModel(e.target.value)}
      >
        {models.map(m => (
          <option key={m.name} value={m.name}>
            {m.name}{m.size_gb ? ` (${m.size_gb} GB)` : ''}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>
          LLM Coaching Prompt
          {mode === 'comparison' && <span className={styles.modeBadge}>Comparison</span>}
          {mode === 'sector' && <span className={styles.modeBadge}>By Sector</span>}
          {mode === 'debrief' && <span className={styles.modeBadge}>Debrief</span>}
        </span>
        <label className={styles.debugToggle} title="Show the raw prompts sent to the LLM">
          <input type="checkbox" checked={debugMode} onChange={toggleDebug} />
          Debug
        </label>
        <button className={styles.closeBtn} onClick={onClose}><X size={14} /></button>
      </div>

      <div className={styles.body}>
        <CapabilitySummary capabilities={capabilities} />

        {mode !== 'comparison' && (
          <div className={styles.backendToggle} title="How to organise the coaching">
            <button
              className={`${styles.backendBtn} ${coachingMode === 'goal' ? styles.backendActive : ''}`}
              onClick={() => setCoachingMode('goal')}
              title="Top 3 cross-corner goals ranked by total time impact (one LLM call)"
            >Top 3 Goals</button>
            <button
              className={`${styles.backendBtn} ${coachingMode === 'corner' ? styles.backendActive : ''}`}
              onClick={() => setCoachingMode('corner')}
              title="Top corners by estimated time loss, with per-corner technique targets (drill-down)"
            >By Corner</button>
            <button
              className={`${styles.backendBtn} ${coachingMode === 'sector' ? styles.backendActive : ''}`}
              onClick={() => setCoachingMode('sector')}
              title="Driver-defined sectors by measured time loss, with pace + consistency read"
            >By Sector</button>
            <button
              className={`${styles.backendBtn} ${coachingMode === 'debrief' ? styles.backendActive : ''}`}
              onClick={() => setCoachingMode('debrief')}
              title="Session summary vs your own history at this track (any car): what went well, where your best passes prove time is available, and how today fits the trend"
            >Debrief</button>
          </div>
        )}

        <div className={styles.buildRow}>
          <button
            className={styles.buildBtn}
            onClick={() => onBuild(coachingMode)}
            disabled={loading || !canBuild}
            title={canBuild ? 'Recompute metrics from the selected laps and build the coaching briefing'
                            : 'Select at least one lap to enable coaching'}
          >
            {briefing
              ? <><RefreshCw size={13} /> Rebuild Prompt</>
              : <><Wrench size={13} /> Build Prompt</>}
          </button>
          {!canBuild && (
            <span className={styles.buildHint}>Select laps to enable coaching.</span>
          )}
        </div>

        {loading && (
          <div className={styles.status}><Loader2 size={13} className={styles.spin} /> Building prompt…</div>
        )}
        {error && (
          <div className={styles.errStatus}><TriangleAlert size={13} /> {error}</div>
        )}
        {!loading && !error && !briefing && canBuild && (
          <div className={styles.status}>Click "Build Prompt" to generate the briefing.</div>
        )}

        {briefing && !loading && (
          <>
            <PriorityList priorities={briefing.priorities} />

            {debugMode && (
              <>
                <CopyableBlock label="System Prompt"          text={briefing.system_prompt} />
                <CopyableBlock label="User Prompt (Briefing)" text={briefing.user_prompt}  />

                {/* Per-corner prompts (By Corner mode only) are tabbed — one per
                    corner — so the 8+ blocks don't crowd the panel. */}
                {briefing.chunks?.length > 0 && (
                  <>
                    <CopyableBlock label="Per-Corner System Prompt" text={briefing.chunk_system_prompt} />
                    <PromptTabs
                      tabs={briefing.chunks
                        .map(c => ({ id: `chunk-${c.id}`, label: c.label, text: c.user_prompt }))
                        .filter(t => t.text)}
                    />
                  </>
                )}
              </>
            )}

            <div className={styles.ollamaSection}>
              <div className={styles.ollamaTitle}>Send for Coaching</div>

              {briefing.chunks?.length > 0 && (
                <div className={styles.backendToggle} title="How many LLM calls to make">
                  <button
                    className={`${styles.backendBtn} ${callMode === 'percorner' ? styles.backendActive : ''}`}
                    onClick={() => setCallMode('percorner')}
                    title="One small call per top corner — more reliable for small local models, slower overall"
                  >Per corner ({briefing.chunks.length} calls)</button>
                  <button
                    className={`${styles.backendBtn} ${callMode === 'single' ? styles.backendActive : ''}`}
                    onClick={() => setCallMode('single')}
                    title="The whole briefing in one call"
                  >One call</button>
                </div>
              )}

              {!forceOllama && (
                <div className={styles.backendToggle}>
                  <button
                    className={`${styles.backendBtn} ${backend === 'ollama' ? styles.backendActive : ''}`}
                    onClick={() => setBackend('ollama')}
                  >Local (Ollama)</button>
                  <button
                    className={`${styles.backendBtn} ${backend === 'claude' ? styles.backendActive : ''}`}
                    onClick={() => setBackend('claude')}
                    disabled={!claudeStatus?.configured}
                    title={claudeStatus?.configured
                      ? 'Use the hosted Claude model'
                      : 'Claude is not configured — set ANTHROPIC_API_KEY in the backend .env'}
                  >Claude</button>
                </div>
              )}

              <div className={styles.ollamaRow}>
                {backend === 'ollama'
                  ? modelPickerContent()
                  : <span className={styles.claudeModel}>{claudeStatus?.model || 'claude-opus-4-8'}</span>}
                {ollamaLoading ? (
                  <button className={styles.cancelBtn} onClick={handleCancel}><Square size={12} /> Stop</button>
                ) : (
                  <button
                    className={styles.ollamaBtn}
                    onClick={handleSend}
                    disabled={backend === 'ollama' && !ollamaModel}
                  >
                    <Send size={13} /> Send
                  </button>
                )}
              </div>

              {backend === 'ollama' && modelsError && (
                <div className={styles.modelsErrHint}>
                  <TriangleAlert size={12} /> Could not reach Ollama ({modelsError}) — type a model name above
                </div>
              )}
              {backend === 'ollama' && !modelsError && models.length === 0 && !modelsLoading && (
                <div className={styles.modelsErrHint}>
                  No models found — run <code>ollama pull llama3</code> to install one
                </div>
              )}

              {(ollamaResponse !== null) && (
                <div className={`${styles.ollamaResponse} ${ollamaStatus === 'error' ? styles.ollamaErr : ''}`}>
                  {ollamaStatus !== 'error' && (
                    <div className={styles.ollamaResponseHeader}>
                      <span>
                        {ollamaLoading
                          ? <span className={styles.streamingDot}>● streaming{streamLabel ? ` ${streamLabel}` : ''}…</span>
                          : <><Flag size={13} /> Coaching feedback</>}
                      </span>
                      <button
                        className={styles.copyBtn}
                        onClick={() => navigator.clipboard.writeText(ollamaResponse).catch(() => {})}
                      >Copy</button>
                    </div>
                  )}
                  <pre className={styles.ollamaPre} ref={responseRef}>{ollamaResponse}</pre>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
